/* ════════════════════════════════════════════════════════════════════════
   PRODUCT BLOB BACKGROUND  ·  product-blob.js
   ────────────────────────────────────────────────────────────────────────
   Fills the decorative .product-blob behind the PDP gallery with the
   selected color's swatch PNG from the CDN, and crossfades + wobbles it
   as the customer changes variant.

   Why this version: the RoarTheme variant picker renders swatches as
   inputs/labels with NO data-swatch-url attribute, so the old click-based
   approach had nothing to read. Instead we derive the color the SAME way
   the product cards do: read the selected variant's color option value,
   handleize it, strip a leading "color_" / "color-", and build:
     https://cdn.shopify.com/s/files/1/0537/7101/0220/files/{color}.png

   Hooks:
   • Listens to the variant picker's `change` (fires for every variant
     change — swatch click, dropdown, size change, etc.)
   • Reads the freshly-selected color from the checked swatch input's value
     or the [data-selected-value] label the theme updates.
   • Falls back to the [data-selected-variant] JSON on initial load.
   ════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0537/7101/0220/files/';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function init() {
    // Only operate on ONE blob to avoid the duplicate-render issue:
    // the template outputs .product-blob in two places (outside and inside
    // the gallery). Prefer the one inside the gallery; fall back to first.
    const galleryBlob = document.querySelector('product-gallery .product-blob, .product__media-wrapper .product-blob');
    const firstBlob = galleryBlob || document.querySelector('.product-blob');
    if (!firstBlob) return;

    // Hide any OTHER .product-blob duplicates so only one shows.
    document.querySelectorAll('.product-blob').forEach((b) => {
      if (b !== firstBlob) b.style.display = 'none';
    });

    const blobs = [firstBlob];

    blobs.forEach(prepBlob);

    const picker = document.querySelector('variant-selects[data-primary]') ||
                   document.querySelector('variant-selects');

    // ── Handleize a color string the same way the cards do ───────────────
    function handleize(str) {
      return (str || '').trim().toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/^color-/, '');
    }

    // ── Figure out which option is the color, return its current value ────
    function currentColorValue() {
      // Strategy 1: a checked swatch input within a swatch fieldset.
      // RoarTheme swatch inputs carry the option value in `value`.
      const swatchFieldset = document.querySelector('.product-form__input--swatch');
      if (swatchFieldset) {
        const checked = swatchFieldset.querySelector('input:checked');
        if (checked && checked.value) return checked.value;
        // Or the visible selected-value label
        const lbl = swatchFieldset.querySelector('[data-selected-value]');
        if (lbl && lbl.textContent) return lbl.textContent;
      }

      // Strategy 2: parse the selected variant JSON and find the color option.
      const json = document.querySelector('variant-selects [data-selected-variant]');
      if (json) {
        try {
          const v = JSON.parse(json.textContent);
          // options is an array like ["Tropical", "24oz"]; we don't know which
          // index is color, so try each and let the CDN 404 filter the rest.
          if (Array.isArray(v.options)) {
            return v.options; // return all; setBlob will try them in order
          }
        } catch (e) { /* ignore */ }
      }
      return null;
    }

    // ── Probe a URL; on success apply to all blobs ───────────────────────
    function tryUrl(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    }

    // ── Resolve a color value (or list) to a working PNG, then paint ─────
    async function paint(colorOrList) {
      if (!colorOrList) return;
      const candidates = Array.isArray(colorOrList) ? colorOrList : [colorOrList];

      for (const c of candidates) {
        const handle = handleize(c);
        if (!handle) continue;
        const url = CDN_BASE + handle + '.png';
        const ok = await tryUrl(url);
        if (ok) {
          blobs.forEach((b) => crossfade(b, ok));
          return;
        }
      }
      // Nothing matched — leave the blob as-is.
    }

    // ── Initial paint ─────────────────────────────────────────────────────
    paint(currentColorValue());

    // ── React to every variant change ────────────────────────────────────
    if (picker) {
      picker.addEventListener('change', () => {
        requestAnimationFrame(() => paint(currentColorValue()));
      });
    }

    // Fallback: delegate clicks on swatch labels/inputs
    document.addEventListener('click', (e) => {
      if (e.target.closest('.product-form__input--swatch')) {
        requestAnimationFrame(() => paint(currentColorValue()));
      }
    });

    // Back/forward cache restore
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) requestAnimationFrame(() => paint(currentColorValue()));
    });
  }

  // ── Blob prep: transitions for the crossfade ────────────────────────────
  function prepBlob(blob) {
    // Do NOT set background-size/position here — the CSS controls those
    // (contain + center for the masked blob). Only ensure a transition.
    const cs = getComputedStyle(blob);
    if (!cs.transitionProperty.includes('opacity')) {
      blob.style.transition = (blob.style.transition ? blob.style.transition + ', ' : '') +
        'opacity 0.35s ease';
    }
    blob.style.willChange = 'transform, opacity';
  }

  // ── Crossfade + wobble a blob to a new image ────────────────────────────
  function crossfade(blob, url) {
    const next = `url('${url}')`;
    if (blob.style.backgroundImage === next) return;

    if (!blob.style.backgroundImage) {
      blob.style.backgroundImage = next; // first paint, no fade
      return;
    }

    const img = new Image();
    img.onload = () => {
      let swapped = false;
      // Read the resting opacity from the CSS var (defaults to 0.4)
      const restOpacity =
        getComputedStyle(blob).getPropertyValue('--blob-opacity').trim() || '0.4';
      const swap = () => {
        if (swapped) return;
        swapped = true;
        blob.style.backgroundImage = next;
        void blob.offsetWidth;
        blob.style.opacity = restOpacity;
      };

      // Shape wobble for the duration of the change.
      // Remove + reflow + add to guarantee the animation restarts even if
      // the class is still present from a rapid previous change.
      blob.classList.remove('is-morphing');
      void blob.offsetWidth; // reflow resets the animation
      blob.classList.add('is-morphing');
      const clearMorph = () => {
        blob.classList.remove('is-morphing');
        blob.removeEventListener('animationend', clearMorph);
      };
      blob.addEventListener('animationend', clearMorph);
      setTimeout(clearMorph, 600);

      blob.style.opacity = '0';
      blob.addEventListener('transitionend', swap, { once: true });
      setTimeout(swap, 400);
    };
    img.onerror = () => { /* keep current */ };
    img.src = url;
  }
})();